import clsx from 'clsx';
import styled, { css } from 'styled-components';
import { SymbolLogo } from '@api3/logos';
import { BreakpointKeys, breakpoints } from '../../../styles/theme';
import Skeleton from '../skeleton';
import { useWindowDimensions } from '../../../hooks';

type Size = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl';

type BreakpointsProps = { [key in BreakpointKeys]?: { size?: Size } };

interface Props extends BreakpointsProps {
  dapiName?: string;
  isError?: boolean;
  isLoading?: boolean;
  size?: Size;
  light?: boolean;
}

const DatafeedLogos = (props: Props) => {
  const { dapiName, light = false, isLoading, isError, size, xs, sm, md, lg } = props;

  const { width } = useWindowDimensions();

  const sizeClass = getSizeClass(width, size, { xs, sm, md, lg });

  const logos = dapiName?.split('/');

  return (
    <Container className={clsx('feed-container', { [sizeClass]: sizeClass })} aria-hidden>
      {!logos || logos.length === 0 || isLoading ? (
        <>
          <Skeleton className={clsx('feed', { 'light-mode': light })} enableAnimation={!isError} />
          <Skeleton className={clsx('feed feed-right loading', { 'light-mode': light })} enableAnimation={!isError} />
        </>
      ) : (
        <>
          <img className="feed" src={SymbolLogo(logos[0], light)} alt={`${logos[0]} logo`} />
          <img className="feed feed-right" src={SymbolLogo(logos[1], light)} alt={`${logos[1]} logo`} />
        </>
      )}
    </Container>
  );
};

// The right logo overlaps the left one and gets a circular notch cut out of its left edge, so it looks like
// it sits behind. Safari doesn't support referencing an SVG <mask> element from CSS mask-image on HTML
// elements (it silently drops the rule, which made the right logo cover the left one), so the notch is cut
// with a radial gradient instead. The extra half pixel keeps the cut edge anti-aliased.
const notch = (radius: number, centerX: number, centerY: number) => {
  const gradient = `radial-gradient(circle at ${centerX}px ${centerY}px, transparent ${radius - 0.5}px, #000 ${radius}px)`;

  return css`
    -webkit-mask-image: ${gradient};
    mask-image: ${gradient};
  `;
};

const XS = css`
  .feed {
    width: 16px;
    height: 16px;
    margin-left: 0;
  }

  .feed-right {
    ${notch(8, -3, 8)}
    margin-left: -3px;
  }
`;

const SM = css`
  .feed {
    width: 21px;
    height: 21px;
    margin-left: 0;
  }

  .feed-right {
    ${notch(10, -5, 10)}
    margin-left: -3px;
  }
`;

const MD = css`
  .feed {
    width: 32px;
    height: 32px;
    margin-left: 0;
  }

  .feed-right {
    ${notch(16, -10, 16)}
    margin-left: -3px;
  }
`;

const LG = css`
  .feed {
    width: 36px;
    height: 36px;
    margin-left: 0;
  }

  .feed-right {
    ${notch(18, -11, 18)}
    margin-left: -4px;
  }
`;

const XL = css`
  .feed {
    width: 42px;
    height: 42px;
    margin-left: 0;
  }

  .feed-right {
    ${notch(21, -12, 21)}
    margin-left: -6px;
  }
`;

const XXL = css`
  .feed {
    width: 64px;
    height: 64px;
    margin-left: 0;
  }

  .feed-right {
    ${notch(32, -20, 32)}
    margin-left: -8px;
  }
`;

const Container = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-size: border-box;

  .feed {
    position: relative;
    border-radius: 50%;

    &.light-mode {
      filter: invert(1);
    }
  }

  // This selector targets the Skeletons
  > * {
    display: inline-flex;
  }

  // Default size
  ${SM}

  @media (min-width: ${breakpoints.sm}px) {
    ${MD}
  }
  @media (min-width: ${breakpoints.md}px) {
    ${LG}
  }
  @media (min-width: ${breakpoints.lg}px) {
    ${XXL}
  }

  // Override default size if specific size is provided
  &.xs {
    ${XS}
  }
  &.sm {
    ${SM}
  }
  &.md {
    ${MD}
  }
  &.lg {
    ${LG}
  }
  &.xl {
    ${XL}
  }
  &.xxl {
    ${XXL}
  }

  .feed-right.loading {
    -webkit-mask-image: none;
    mask-image: none;
  }
`;

const getSizeClass = (width: number, size?: Size, sizeProps?: BreakpointsProps) => {
  let sizeClass: Size | undefined;

  const { xs, sm, md, lg } = sizeProps || {};

  if (width >= breakpoints.lg) {
    sizeClass = lg?.size || md?.size || sm?.size || xs?.size;
  }
  if (width < breakpoints.lg) {
    sizeClass = md?.size || sm?.size || xs?.size;
  }
  if (width < breakpoints.md) {
    sizeClass = sm?.size || xs?.size;
  }
  if (width < breakpoints.sm) {
    sizeClass = xs?.size;
  }

  return sizeClass || size || '';
};

export default DatafeedLogos;
